<template>
    <CRow>
        <CCol col="12" xl="12">
            <CCard>
                <CCardHeader>
                    <strong>Редактировать сегмент: {{ segment.name }}</strong>
                </CCardHeader>
                <CCardBody>
                    <div class="table__preloader" v-if="loading">
                        <CSpinner color="primary"/>
                    </div>
                    <div v-show="!loading">
                        <CForm @submit.prevent="updateSegment">
                            <CRow class="mb-4">
                                <CCol cols="12" md="6">
                                    <CInput
                                        v-model="editForm.name"
                                        label="Название сегмента"
                                        placeholder="Введите название сегмента"
                                        :invalid="errors.name"
                                        required
                                    />
                                    <div v-if="errors.name" class="invalid-feedback d-block">
                                        Название сегмента обязательно для заполнения
                                    </div>
                                </CCol>
                                <CCol cols="12" md="6">
                                    <CRow>
                                        <CCol cols="6">
                                            Активен:
                                        </CCol>
                                        <CCol cols="6">
                                            <CSwitch
                                                color="dark"
                                                shape="pill"
                                                variant="opposite"
                                                :checked.sync="editForm.enabled"
                                                class="ml-3" />
                                        </CCol>
                                    </CRow>
                                </CCol>
                            </CRow>

                            <CRow class="mb-4">
                                <CCol cols="12" md="6">
                                    <label class="form-label">Частота обновления сегмента</label>
                                    <CSelect
                                        v-model="editForm.update_frequency_hours"
                                        :options="frequencyOptions"
                                        placeholder="Выберите частоту обновления"
                                    />
                                    <small class="form-text text-muted">
                                        Как часто автоматически обновлять список клиентов в сегменте (для проверки актуального статуса подписки на пуши и других изменений)
                                    </small>
                                </CCol>
                            </CRow>

                            <CRow class="mb-4">
                                <CCol cols="12">
                                    <CTextarea
                                        v-model="editForm.filtersJson"
                                        label="Фильтры (JSON)"
                                        placeholder="Введите фильтры в формате JSON"
                                        rows="15"
                                        :invalid="errors.filters"
                                    />
                                    <div v-if="errors.filters" class="invalid-feedback d-block">
                                        {{ errors.filters }}
                                    </div>
                                    <small class="form-text text-muted">
                                        Фильтры должны быть в корректном JSON формате. При изменении фильтров список клиентов будет автоматически обновлен.
                                    </small>
                                </CCol>
                            </CRow>

                            <CRow class="mb-4" v-if="segment.sql_query">
                                <CCol cols="12">
                                    <label class="form-label">SQL запрос (только для чтения)</label>
                                    <pre class="bg-dark p-3 rounded text-light sql-query">{{ segment.sql_query }}</pre>
                                    <small class="form-text text-muted">
                                        SQL запрос, сформированный на основе текущих фильтров сегмента. Обновляется автоматически при сохранении изменений.
                                    </small>
                                </CCol>
                            </CRow>

                            <CRow>
                                <CCol cols="12">
                                    <CButton
                                        type="submit"
                                        color="primary"
                                        class="mr-2"
                                        :disabled="saving">
                                        {{ saving ? 'Сохранение...' : 'Сохранить изменения' }}
                                    </CButton>
                                    <CButton
                                        color="secondary"
                                        class="mr-2"
                                        @click="$router.go(-1)">
                                        Отмена
                                    </CButton>
                                    <CButton
                                        color="info"
                                        @click="previewFilters"
                                        :disabled="!isValidJson">
                                        Предварительный просмотр
                                    </CButton>
                                </CCol>
                            </CRow>
                        </CForm>

                        <div v-if="showPreview" class="mt-4">
                            <hr>
                            <h5>Предварительный просмотр фильтров</h5>
                            <pre class="bg-light p-3 rounded text-black">{{ formattedFilters }}</pre>
                        </div>
                    </div>
                </CCardBody>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
    import Vue from 'vue';
    const { formatDate } = require('../../utils/views');

    export default {
        name: 'SegmentEdit',

        data() {
            return {
                loading: true,
                saving: false,
                segment: {},
                editForm: {
                    name: '',
                    enabled: true,
                    filtersJson: '',
                    update_frequency_hours: null
                },
                errors: {
                    name: false,
                    filters: null
                },
                showPreview: false,
                frequencyOptions: [
                    { value: null, label: 'Стандартная (раз в неделю)' },
                    { value: 1, label: 'Каждый час' },
                    { value: 2, label: 'Каждые 2 часа' },
                    { value: 3, label: 'Каждые 3 часа' },
                    { value: 6, label: 'Каждые 6 часов' },
                    { value: 12, label: 'Каждые 12 часов' },
                    { value: 24, label: 'Каждые 24 часа (раз в день)' },
                    { value: 48, label: 'Каждые 48 часов (раз в 2 дня)' },
                    { value: 72, label: 'Каждые 72 часа (раз в 3 дня)' },
                    { value: 168, label: 'Каждые 168 часов (раз в неделю)' }
                ]
            };
        },

        computed: {
            isValidJson() {
                try {
                    JSON.parse(this.editForm.filtersJson);
                    return true;
                } catch (e) {
                    return false;
                }
            },
            formattedFilters() {
                try {
                    return JSON.stringify(JSON.parse(this.editForm.filtersJson), null, 2);
                } catch (e) {
                    return 'Некорректный JSON';
                }
            }
        },

        async mounted() {
            await this.fetch();
            this.loading = false;
        },

        methods: {
            formatDate,

            async fetch() {
                try {
                    const segmentId = this.$route.params.id;
                    const response = await Vue.axios.get(`/segments/${segmentId}`);
                    this.segment = response.data;
                    
                    // Заполняем форму данными сегмента
                    this.editForm.name = this.segment.name;
                    this.editForm.enabled = this.segment.enabled;
                    this.editForm.filtersJson = JSON.stringify(this.segment.filters, null, 2);
                    this.editForm.update_frequency_hours = this.segment.update_frequency_hours;
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка загрузки сегмента.');
                    console.error(err);
                    this.$router.push('/segments');
                }
            },

            validateForm() {
                this.errors = {
                    name: false,
                    filters: null
                };

                if (!this.editForm.name.trim()) {
                    this.errors.name = true;
                    return false;
                }

                try {
                    JSON.parse(this.editForm.filtersJson);
                } catch (e) {
                    this.errors.filters = 'Некорректный JSON формат фильтров';
                    return false;
                }

                return true;
            },

            async updateSegment() {
                if (!this.validateForm()) {
                    return;
                }

                this.saving = true;
                
                try {
                    const segmentId = this.$route.params.id;
                    const updateData = {
                        name: this.editForm.name.trim(),
                        enabled: this.editForm.enabled,
                        filters: JSON.parse(this.editForm.filtersJson),
                        update_frequency_hours: this.editForm.update_frequency_hours
                    };

                    await Vue.axios.put(`/segments/${segmentId}`, updateData);
                    
                    this.$root.$emit('showSuccess', 'Сегмент успешно обновлен!');
                    this.$router.push(`/segments/${segmentId}`);
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка при обновлении сегмента.');
                    console.error(err);
                } finally {
                    this.saving = false;
                }
            },

            previewFilters() {
                this.showPreview = !this.showPreview;
            }
        }
    };
</script>

<style scoped>
pre {
    max-height: 400px;
    overflow-y: auto;
}

.sql-query {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    max-height: 200px;
    overflow-y: auto;
    word-break: break-all;
    white-space: pre-wrap;
}
</style> 